import { Controller } from "@hotwired/stimulus"
import { refreshPhone } from "../studio_phone"

// spots.by's studio forms (29 Oct 2026): every pill and switch saves the form
// the moment it changes, then reloads the page in the phone beside it. The
// form also mirrors its current values on itself (data-header-layout="left"…)
// so the stylesheet can grey the rows a choice makes pointless.
//
// Presets are buttons carrying a set of values: they check the matching
// inputs, then save once.
export default class extends Controller {
  static targets = ["status"]
  static values = { part: { type: String, default: "top" } }

  connect() {
    this.mirror()
    this.markPreset()
  }

  disconnect() { clearTimeout(this.timer) }

  save() {
    this.mirror()
    this.markPreset()
    const token = document.querySelector("meta[name=csrf-token]")?.content
    fetch(this.element.action, {
      method: "PATCH",
      body: new FormData(this.element),
      headers: { "X-CSRF-Token": token, Accept: "application/json" },
      credentials: "same-origin"
    }).then((response) => {
      if (!response.ok) throw new Error(response.status)
      return response.json()
    }).then(({ version }) => {
      this.reloadPhone(version)
      this.flash("Saved")
    }).catch(() => this.flash("Not saved, try again"))
  }

  preset(event) {
    // Stimulus parses a JSON param itself. The inputs may live in another
    // form of the page (Header: the presets sit above the profile): each
    // form touched saves once.
    const raw = event.params.values
    const values = typeof raw === "string" ? JSON.parse(raw) : (raw || {})
    const forms = new Set()
    Object.entries(values).forEach(([key, value]) => {
      document.querySelectorAll(`[name="design[${key}]"]`).forEach((input) => {
        if (input.type === "radio") input.checked = input.value === String(value)
        else if (input.type === "checkbox") input.checked = value === true || value === "1"
        else return
        if (input.form) forms.add(input.form)
      })
    })
    forms.forEach((form) => form.dispatchEvent(new Event("change", { bubbles: true })))
    this.markPreset()
  }

  // The preset the form now matches, every value it sets that this form
  // holds; none when a choice made since departs from all of them.
  markPreset() {
    document.querySelectorAll("[data-studio-target~=preset]").forEach((button) => {
      const raw = button.dataset.studioValuesParam
      const values = raw ? JSON.parse(raw) : {}
      const matches = Object.entries(values).every(([key, value]) => {
        const inputs = [...document.querySelectorAll(`[name="design[${key}]"]`)].filter((input) => input.type === "radio" || input.type === "checkbox")
        if (inputs.length === 0) return true
        const radio = inputs.find((input) => input.type === "radio" && input.checked)
        if (radio) return radio.value === String(value)
        const box = inputs.find((input) => input.type === "checkbox")
        return box ? box.checked === (value === true || value === "1") : false
      })
      button.setAttribute("aria-pressed", String(matches))
    })
  }

  mirror() {
    this.element.querySelectorAll("input[type=radio]:checked, input[type=checkbox]").forEach((input) => {
      const key = input.name.match(/^design\[(\w+)\]$/)?.[1]
      if (key) this.element.dataset[key.replace(/_(\w)/g, (_, c) => c.toUpperCase())] = input.type === "checkbox" ? String(input.checked) : input.value
    })
  }

  // Map and Listing change what lies under the top: the phone swaps its
  // places section in place, without reloading. The Agent tab's dock sits
  // outside both: "full" reloads the phone.
  reloadPhone(version) {
    if (this.partValue === "full") return refreshPhone(version, { full: true })
    refreshPhone(version, { part: this.partValue })
  }

  flash(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    clearTimeout(this.timer)
    this.timer = setTimeout(() => { this.statusTarget.textContent = "" }, 1600)
  }
}
